import {
  DrawerNavigationCategoryTree_Item,
  DrawerNavigationCategoryTree_TreeItem,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation/v1/drawer_navigation.types';
import {useTranslation} from '@aboutyou/i18n';
import {TrackAyOutletLink, useTrackOriginClick} from '@aboutyou/tracking';
import {
  COLORS,
  CategoryTile,
  GenericIconName,
  UnstyledLinkLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import couponTilePattern from '../../../features/Coupons/assets/couponTilePattern.svg';
import {useOutletLinkConfig} from '../../../features/OutletLink/useOutletLinkConfig';
import {useIsSelectedDiscountAppOnly} from '../../Campaigns/SelectedDiscounts/useSelectedDiscount';
import {useSelectedDiscountColor} from '../../Campaigns/SelectedDiscounts/useSelectedDiscountColor';
import {useSelectedDiscountUrl} from '../../Campaigns/SelectedDiscounts/useSelectedDiscountUrl';
import {useNavigationCategory} from '../useNavigationCategory';
import {AllDealsTile} from './shared/AllDealsTile';
import {CategoryItemWrapper} from './shared/CategoryItemWrapper';
import {CategoryTreeNavigationItem} from './shared/CategoryTreeNavigationItem';
import {StyledMenu, UnstyledList} from './shared/StyledComponents';

interface FirstLevelCategoryTreeProps {
  isTransitioning: boolean;
  items: DrawerNavigationCategoryTree_Item[];
  onClick?: () => void;
  onCategoryClick?: React.MouseEventHandler<HTMLLIElement>;
  makeInert?: boolean;
}

export const FirstLevelCategoryTree: React.FC<FirstLevelCategoryTreeProps> = ({
  isTransitioning,
  items,
  onClick,
  onCategoryClick,
  makeInert,
}) => {
  const {t} = useTranslation();
  const {setCurrentCategoryId} = useNavigationCategory();
  const trackOrigin = useTrackOriginClick();
  const trackCategoryClick = trackOrigin('category_list');

  const {ayOutletUrl, outletLabel, showOutletLink} =
    useOutletLinkConfig('catNav_mWeb');

  const discountUrl = useSelectedDiscountUrl();
  const discountColor = useSelectedDiscountColor();
  const isAppOnly = useIsSelectedDiscountAppOnly();

  return (
    <Nav inert={Boolean(makeInert)}>
      <StyledMenu
        data-testid="firstLevelCategoryNavigation"
        data-is-transitioning={isTransitioning}
      >
        <UnstyledList data-increase-grid-gap={false}>
          {discountUrl && !isAppOnly && (
            <AllDealsTile
              color={discountColor!}
              linkTarget={`${discountUrl.url}&hasCampaignReduction=true`}
            />
          )}

          {items.map(item =>
            item.item?.$case === 'treeItem' ? (
              <CategoryItemWrapper
                key={item.item.treeItem.category.id}
                item={item.item.treeItem}
                onCategoryClick={onCategoryClick}
                handleClick={e => {
                  onClick?.();
                  const currentItem = item.item as {
                    $case: 'treeItem';
                    treeItem: DrawerNavigationCategoryTree_TreeItem;
                  };

                  if (!currentItem.treeItem.category.isLeafCategory) {
                    trackCategoryClick();
                    e.preventDefault();
                    setCurrentCategoryId(currentItem.treeItem.category.id);
                  }
                }}
              >
                <CategoryTreeNavigationItem item={item.item.treeItem} />
              </CategoryItemWrapper>
            ) : (
              <li tabIndex={-1}>
                <UnstyledLinkLinaria
                  data-testid="couponTile"
                  to={item.item!.couponItem.link.url}
                  aria-label={undefined}
                >
                  <CouponCategoryTile
                    label={
                      <AllCouponItemsLabel>
                        {t('All Coupon Items')}
                      </AllCouponItemsLabel>
                    }
                    backgroundColor={COLORS.PrimaryCoupon}
                    labelColor="Text.White"
                  />
                </UnstyledLinkLinaria>
              </li>
            ),
          )}
          {showOutletLink && (
            <TrackAyOutletLink Comp="li">
              <UnstyledLinkLinaria
                to={ayOutletUrl}
                data-testid="ayOutletLink-offcanvasFirstlevelCategory"
                aria-label={undefined}
              >
                <CouponCategoryTile
                  label={outletLabel}
                  thumbnail={{
                    icon: {
                      size: 'S',
                      asset: GenericIconName.ExternalLink,
                    },
                  }}
                />
              </UnstyledLinkLinaria>
            </TrackAyOutletLink>
          )}
        </UnstyledList>
      </StyledMenu>
    </Nav>
  );
};

const Nav = styled.nav`
  margin: 16px 0 10px 0;
`;

const AllCouponItemsLabel = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const CouponCategoryTile = styled(CategoryTile)`
  background-position: center;
  background-image: url('${couponTilePattern}');
  background-size: cover;
`;
